/* eslint-disable @typescript-eslint/naming-convention */

import { ICommonFormRef } from 'KnmErknmPackage/MainModule/interfaces/ICommonFormRef';

const idRegex = /\[\d+]/gi;

const isActField = (fieldPath: string) => {
  // Если это поле решения - игнорируем
  if (fieldPath.includes('resultDecisions')) {
    return false;
  }

  // Если это поле акта - обрабатываем
  if (fieldPath.includes('.act.') || fieldPath.includes('act.') || fieldPath.includes('acts[{id}]')) {
    return true;
  }

  // Для корневых полей актов
  if (fieldPath === 'acts' || fieldPath === 'act' || fieldPath === 'organization.act') {
    return true;
  }

  return false;
};

export const getIsDirtyAct = (
  dirtyFields:
    | {
        [key: string]: boolean;
      }
    | undefined,
  index?: number,
  isPm?: boolean | undefined,
  isDecision?: boolean | undefined,
  formRef?: ICommonFormRef,
  modified?:
    | {
        [key: string]: boolean;
      }
    | undefined,
) => {
  if (!dirtyFields || Object.keys(dirtyFields).length === 0) {
    return {
      wasEditFiled: false,
      wasEditWithoutViolationsFixed: false,
      wasEditOnlyViolations: false,
    };
  }

  let fields: string[] = fieldsKnm;
  let firstKey: string = 'acts';

  if (isPm) {
    fields = fieldsPm;
    firstKey = 'act';
  }

  if (isDecision) {
    fields = fieldsDecision;
    firstKey = 'act';
  }

  /**
   * Добавлена проверка на modified, .т.к. при смене
   * "Нарушение не устранено" (потенциально и других полей), на значение
   * которое было изначально, состояние dirty для поля снимается, но остается
   * dirty для родительских зарегистрированных полей.
   */
  const modifiedFields =
    modified &&
    Object.entries(modified)
      .filter(item => item[1] === true)
      .map(item => item[0].replaceAll(idRegex, '[{id}]'));

  // 1. Определяем, есть ли изменения в blackList полях
  const hasBlackListChanges = !!Object.keys(dirtyFields).find(i => {
    const regexpS = i.replaceAll(idRegex, '[{id}]');

    if (!isActField(regexpS)) {
      return false;
    }

    // Если есть необходимость проверять modified
    if (modified) {
      // Проверяем, что это поле есть в modified (отсеивает изменившиеся родительские блоки)
      const isInModifiedFields = !!modifiedFields?.find(item => item === regexpS);

      if (!isInModifiedFields) return false;
    }

    return !!blackList.find(j => regexpS.indexOf(j) > -1);
  });

  // 2. Определяем, есть ли изменения в НЕ-blackList полях
  const hasNonBlackListChanges = !!Object.keys(dirtyFields).find(key => {
    const fieldNameTemplate = key
      .split('.')
      .map(item => (item.indexOf(firstKey) === -1 ? item?.replace(idRegex, '[{id}]') : item))
      .join('.');

    const regexpS = key.replaceAll(idRegex, '[{id}]');

    // Если есть необходимость проверять modified
    if (modified) {
      // Проверяем, что это поле есть в modified (отсеивает изменившиеся родительские блоки)
      const isInModifiedFields = !!modifiedFields?.find(item => item === regexpS);

      if (!isInModifiedFields) return false;
    }

    const { initial, value } = formRef?.current.getFieldState(key) || {};

    let isDeleteLastElementFromArray = false;

    if (
      (Array.isArray(initial) || Array.isArray(value)) &&
      (initial === undefined || (Array.isArray(initial) && initial.length < 1)) &&
      Array.isArray(value) &&
      value.length < 1
    ) {
      isDeleteLastElementFromArray = true;
    }

    const isInFields = !!fields.find(f => {
      const fieldNameInList = index !== undefined ? f.replace('[{id}]', `[${index}]`) : f;

      return fieldNameInList === fieldNameTemplate && !isDeleteLastElementFromArray;
    });

    if (!isInFields) return false;

    // Проверяем, что это поле НЕ в blackList
    const isBlackList = !!blackList.find(j => regexpS.indexOf(j) > -1);

    return !isBlackList;
  });

  const wasEditWithoutViolationsFixed = !hasBlackListChanges;
  const wasEditFiled = hasBlackListChanges || hasNonBlackListChanges;
  const wasEditOnlyViolations = hasBlackListChanges && !hasNonBlackListChanges;

  return {
    wasEditFiled,
    wasEditWithoutViolationsFixed,
    wasEditOnlyViolations,
  };
};

const blackList = [
  'violatedMandatory[{id}].violations[{id}].isFixed',
  'violatedMandatory[{id}].violations[{id}].isAppealed',
  'violatedMandatory[{id}].violations[{id}].isUnfixable',
  'violatedMandatory[{id}].violations[{id}].isFixedOnPlace',
  'violatedMandatory[{id}].violations[{id}].isViolationResolved',
  'violatedMandatory[{id}].violations[{id}].isViolationResolvedNote',
  'violatedMandatory[{id}].violations[{id}].repeatCorrected',
  'violatedMandatory[{id}].violations[{id}].repeatCorrectedInEvent',
];

const fieldsKnm = [
  'acts[{id}].familiarizationNotifications',
  'acts[{id}].familiarizationNotifications[{id}]',
  'acts[{id}].familiarizationNotifications[{id}].knmNoticeType',
  'acts[{id}].familiarizationNotifications[{id}].noticeDate',
  'acts[{id}].familiarizationNotifications[{id}].note',
  'acts[{id}].violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isFixed',
  'acts[{id}].violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isAppealed',
  'acts[{id}].violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isUnfixable',
  'acts[{id}].violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isFixedOnPlace',
  'acts[{id}].violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isViolationResolved',
  'acts[{id}].violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isViolationResolvedNote',
  'acts[{id}].violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].repeatCorrected',
  'acts[{id}].violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].repeatCorrectedInEvent',
  'acts[{id}].remarks',
  'acts[{id}].remarks[{id}].text',
  'acts[{id}].remarks[{id}].textReactQuillEditor',
  'acts[{id}].remarks[{id}].signerFullName',
  'acts[{id}].remarks[{id}].signerPosition',
  'acts[{id}].remarks[{id}].date',
];

const fieldsPm = [
  'act.familiarizationNotifications',
  'act.familiarizationNotifications[{id}]',
  'act.familiarizationNotifications[{id}].knmNoticeType',
  'act.familiarizationNotifications[{id}].noticeDate',
  'act.familiarizationNotifications[{id}].note',
  'act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isFixed',
  'act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isAppealed',
  'act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isUnfixable',
  'act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isFixedOnPlace',
  'act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isViolationResolved',
  'act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isViolationResolvedNote',
  'act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].repeatCorrected',
  'act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].repeatCorrectedInEvent',
  'act.remarks',
  'act.remarks[{id}].text',
  'act.remarks[{id}].textReactQuillEditor',
  'act.remarks[{id}].text',
  'act.remarks[{id}].textReactQuillEditor',
  'act.remarks[{id}].signerFullName',
  'act.remarks[{id}].signerPosition',
  'act.remarks[{id}].date',
];

const fieldsDecision = [
  'organization.act.familiarizationNotifications',
  'organization.act.familiarizationNotifications[{id}]',
  'organization.act.familiarizationNotifications[{id}].knmNoticeType',
  'organization.act.familiarizationNotifications[{id}].noticeDate',
  'organization.act.familiarizationNotifications[{id}].note',
  'organization.act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isFixed',
  'organization.act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isAppealed',
  'organization.act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isUnfixable',
  'organization.act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isFixedOnPlace',
  'organization.act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].repeatCorrected',
  'organization.act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].repeatCorrectedInEvent',
  'organization.act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isViolationResolved',
  'organization.act.violatedMandatoryRequirements[{id}].violatedMandatory[{id}].violations[{id}].isViolationResolvedNote',
  'organization.act.remarks',
  'organization.act.remarks[{id}].text',
  'organization.act.remarks[{id}].textReactQuillEditor',
  'organization.act.remarks[{id}].text',
  'organization.act.remarks[{id}].textReactQuillEditor',
  'organization.act.remarks[{id}].signerFullName',
  'organization.act.remarks[{id}].signerPosition',
  'organization.act.remarks[{id}].date',
];
